<template>
  <div
    class="ovp-configurator ovp-a11y-container"
    :app-language="selectedLanguage"
    :app-name="$props.source"
    :app-version="version$"
  >
    <system-notifications :language="selectedLanguage" />

    <router-view />

    <a-b-config />
    <div id="modals"></div>
    <the-confirmation />
    <the-appointment />
    <auth-modal />

    <the-loading />
    <global-error-handler />
    <the-snackbar />
  </div>
</template>

<script setup>
  import { inject, onMounted } from 'vue'

  import useApplication from '@shared/hooks/useApplication'
  import useI18n from '@shared/hooks/useI18n'

  import TheLoading from '@shared/components/Container/Loading'
  import GlobalErrorHandler from '@shared/components/Container/GlobalErrorHandler'
  import TheSnackbar from '@shared/components/Basic/Snackbar'
  import SystemNotifications from '@shared/components/Container/SystemNotifications'
  import ABConfig from '@shared/components/Container/ABConfig'
  import AuthModal from '@shared/components/Authentication/AuthModal'
  import TheAppointment from '@shared/components/Modal/Appointment'
  import TheConfirmation from '@shared/components/Modal/Confirmation'

  import { SOURCE } from '@shared/config/events'

  import '@shared/assets/css/import.css'

  // HOOKS
  const { preferedColorScheme } = useApplication()
  const { selectedLanguage } = useI18n()

  // INIT
  const version$ = inject('version$')
  defineProps({
    source: {
      type: String,
      default: SOURCE.CONFIGURATOR,
    },
  })

  // LIFECYCLE HOOKS
  onMounted(() => {
    document.querySelector(':root').style.setProperty('--color-scheme', preferedColorScheme())
  })
</script>

<style name="global">
:root {
  /*
    New stacking contexts can be formed on an element in one of three ways:
    - When an element is the root element of a document (the <html> element)
    - When an element has a position value other than static and a z-index value other than auto
    - When an element has an opacity value less than 1
  */
  --dvp-stack-level-element: 1;
  --dvp-stack-level-footer: 1;
  --dvp-stack-level-header: 2;
  --dvp-stack-level-popup-menu: 2;
  --dvp-stack-level-modal: 3;
  --dvp-stack-level-modal-stacked: 4; /* exception for medical-office-search modal as it can be opened within another modal */
  --dvp-stack-level-popup-notification: 5;
  --dvp-stack-level-tooltip: 6;
  --dvp-stack-level-system-notification: 7;
  --dvp-stack-level-screen-blocker: 8;

  color-scheme: var(--color-scheme, 'light');
}

#chat-ui {
  position: relative;
  z-index: var(--dvp-stack-level-popup-menu);
}

*,
*:after,
*:before {
  box-sizing: border-box;
}

*:focus {
  outline: none;
}

html {
  overflow-y: scroll;
}

body {
  font-family: var(--font-family-open-sans);
  font-style: var(--font-style-default);
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-regular-lg);
  letter-spacing: var(--letter-spacing-regular-lg);
  color: var(--Text-Primary);
  background-color: var(--surface);

  margin: 0;
  /* FLEX-2954 prevent larger fonts for mobile landscape */
  -webkit-text-size-adjust: none;
}

p,
h1,
h2,
h3 {
  margin-block-start: 0;
  margin-block-end: 0;
}

a {
  color: inherit;
  text-decoration-line: none;
}

button,
input,
select,
textarea {
  font-family:
    Open Sans,
    sans-serif !important;
}

.tippy-box[data-theme~='dvp'] {
  min-width: 144px;
  padding: var(--fixed-spacing-fix-06);
  background-color: var(--surface);
  border-radius: var(--fixed-border-radius-fix-02);
  box-shadow: var(--elevation-level-5);

  .tippy-content {
    font-weight: var(--font-weight-regular);
    font-size: var(--font-size-regular-sm);
    letter-spacing: var(--letter-spacing-regular-sm);
    color: var(--on-surface);
    padding: 0;
  }

  .tippy-content ul,
  .tippy-content ol {
    margin: 0 0 var(--fixed-spacing-fix-04) var(--fixed-spacing-fix-06);
    padding: 0;
    list-style-position: outside;
  }

  .tippy-content li:not(:last-child) {
    margin: 0 0 var(--fixed-spacing-fix-01);
  }

  .tippy-arrow {
    color: var(--surface);

    &::before {
      transform: scale(1.25);
    }
  }
}

.tippy-box[data-theme~='card'] {
  background-color: var(--surface);
  border-radius: var(--fixed-border-radius-fix-02);
  box-shadow: var(--elevation-level-5);
  padding: var(--fixed-spacing-fix-02);
  margin: 0;
  & > .tippy-content {
    padding: 0;
    margin: 0;
    height: max-content;

    & > .card__image {
      margin: 0;
      padding: 0;
      width: 100%;
    }
  }

  .tippy-arrow {
    color: var(--surface);

    &::before {
      transform: scale(1.25);
    }
  }
}

.tippy-box[data-placement='top-end'] > {
  .tippy-arrow {
    right: 20px !important;
    left: initial !important;
  }
}

.the-transition-enter-active,
.the-transition-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.the-transition-enter-from,
.the-transition-leave-to {
  opacity: 0;
}
</style>

<style scoped>
.ovp-configurator {
  display: grid;
  grid-template-areas:
    'alerts'
    'header'
    'content'
    'footer';
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto 1fr auto;
  height: 100vh;
}
</style>
